import { useMemo, useContext } from 'react';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import GravSYSOptionsQuery from '../Queries/GravSYSOptionsQuery.gql';
import { useQuery } from '@apollo/client';
import GravTitelKort from '../GravTitelKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';

const GravModel = clientAvenyModel.GA.BusinessModels.GAGrav;

interface IKartaProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

export const Karta: React.FC<IKartaProps> = ({ sidNr, nodeData, fullPage }) => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  // Hämta MyCarta-konfiguration från SYSOptions
  const { data: sysOptionsData, loading: optionsLoading } = useQuery(
    GravSYSOptionsQuery,
    {
      variables: { db: activeDatabase || '' },
      skip: !activeDatabase,
    }
  );

  const myCartaUrl = useMemo(() => {
    // Hjälpfunktion för att hämta ett SYSOption-värde
    const getOption = (key: string): string => {
      const options = sysOptionsData?.db?.SYSOptions_All ?? [];
      const option = options.find((opt: any) => opt.OptionKey === key);
      return option?.OptionData || '';
    };

    const serverUrl = getOption('GA_MYCARTA_SERVER');
    const mapName = getOption('GA_MYCARTA_WEBAVENYGRAV_KARTDEFFILNAMN');
    const myCartaLicence = getOption('SYS_PROGOPT_Web myCarta');

    // Avgör om WebMap eller eMap ska användas
    const usingWebMap = myCartaLicence === 'Y';

    if (!serverUrl || !mapName) {
      return '';
    }

    // Bygg bas-URL
    const cleanServerUrl = serverUrl.replace(/\/+$/, '');
    const baseUrl = usingWebMap
      ? `${cleanServerUrl}/Webmap/`
      : `${cleanServerUrl}/eMap/`;

    // Utan gravdata - visa bara kartan
    if (!nodeData) {
      return usingWebMap
        ? `${baseUrl}#m=${mapName}`
        : `${baseUrl}?map=${mapName}`;
    }

    const kvarterKod = nodeData.KvarterKod || '';
    const kyrkogard = kvarterKod.replace(/\d+$/, '');
    const gravNummer = nodeData.GravNr || '';

    if (!kyrkogard || !gravNummer) {
      return usingWebMap
        ? `${baseUrl}#m=${mapName}`
        : `${baseUrl}?map=${mapName}`;
    }

    // Bygg URL med rätt format beroende på karttyp
    let url: string;
    if (usingWebMap) {
      // WebMap format: #m=mapname,Kg=kyrkogård,gravplatsnr=gravnummer
      url = `${baseUrl}#m=${mapName},Kg=${encodeURIComponent(
        kyrkogard
      )},gravplatsnr=${encodeURIComponent(gravNummer)}`;
    } else {
      // eMap format: ?map=mapname&Kg=kyrkogård&gravplatsnr=gravnummer
      url = `${baseUrl}?map=${encodeURIComponent(
        mapName
      )}&Kg=${encodeURIComponent(kyrkogard)}&gravplatsnr=${encodeURIComponent(
        gravNummer
      )}`;
    }

    return url;
  }, [nodeData, sysOptionsData?.db?.SYSOptions_All]);

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Karta'}
      collectionName={GravModel.CollectionName}
    >
      <ObjektVyKolumner>
        <ObjektVyKolumn fullPage={fullPage}>
          <GravTitelKort nodeData={nodeData} />
          <ObjektVyKort
            className="position-relative"
            backgroundColor={'white'}
            caption="Kartvy"
          >
            {optionsLoading ? (
              <div>Laddar karta...</div>
            ) : !myCartaUrl ? (
              <div>Kartkonfiguration saknas</div>
            ) : (
              <iframe
                src={myCartaUrl}
                style={{
                  width: '100%',
                  height: '600px',
                  border: '1px solid #c0c0c0',
                  borderRadius: '4px',
                }}
                title="MyCarta Kartvy"
                allowFullScreen
              />
            )}
          </ObjektVyKort>
        </ObjektVyKolumn>
      </ObjektVyKolumner>
    </ObjektVySida>
  );
};

export default Karta;
